import { types, clone } from 'mobx-state-tree';
import { getOr } from 'lodash/fp';
import { flow } from 'mobx';
import { EditableCells } from '../../models/ListingsPage';
import { monitoringStatusString } from '../../constants/listings/set-monitoring-status-list';

const statusName = ['active', 'ended', 'outOfStock', 'removed'];

const Editable = types
  .model({
    editedItems: types.optional(types.map(EditableCells), {}),
    isEditMode: false,
  })
  .volatile(self => ({
    getTitleEditValue(id) {
      return getOr(self.tableDataAsObject[id].title, 'title.value', self.editedItems.get(id));
    },
    getSalePriceEditValue(id) {
      const retval =
        self?.editedItems?.get(id)?.salePrice?.value ||
        self?.tableDataAsObject[id]?.pricingStrategy?.salePrice ||
        0;
      return retval;
    },
    getTemplateEditValue(id) {
      const templateId = getOr(
        self.tableDataAsObject[id].templateId,
        'templateId.value',
        self.editedItems.get(id),
      );
      return self.getTemplateName(templateId);
    },
    ifItemExists(id) {
      return self.editedItems.get(id) !== undefined;
    },
    getItem(id) {
      if (self.ifItemExists(id)) {
        return self.editedItems.get(id);
      }
      self.editedItems.set(id, {});
      return self.editedItems.get(id);
    },
    get ifSaveBtnIsDisabled() {
      return self.editedItems.size === 0;
    },
  }))
  .actions(self => ({
    setTitle(id, value) {
      const item = self.getItem(id);
      item.title.value = value;
      self.checkIfValuesAreNotEqual(item, id);
    },
    setSalePrice(id, value) {
      const item = self.getItem(id);
      item.salePrice.value = parseFloat(value);
      self.checkIfValuesAreNotEqual(item, id);
    },
    setTemplate(id, value) {
      const item = self.getItem(id);
      item.templateId.value = value;
      self.checkIfValuesAreNotEqual(item, id);
    },
    updateItems() {
      const itemsToUpdate = clone(self.editedItems);
      itemsToUpdate.forEach((item, id) =>
        self.sendUpdateReqForSingleItem(item.changedFieldsArray, id),
      );
      self.cancelEditing();
    },
    sendUpdateReqForSingleItem: flow(function* sendUpdateReqForSingleItem(itemFields, id) {
      try {
        const url = `${self.BASE_URL}/items/${id}`;
        const { data } = yield self.transport5007.patch(
          url,
          itemFields.map(({ op, path, value }) => ({ op, path, value })),
          {},
          self.headers,
        );
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: itemFields.length
            ? 'Listings updated successfully'
            : 'Listing updated successfully',
        });
        self.updateOriginItemCells(itemFields, id);
        self.setIsUpdated(true);
        return data;
      } catch (err) {
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'Listing could not be updated',
        });
        console.log(err);
        return { updateCompleted: false };
      }
    }),
    updateOriginItemCells(itemFields, id) {
      if (!self.tableDataAsObject[id]) {
        return;
      }
      itemFields.forEach(({ fieldKey, value }) => {
        const fieldArr = fieldKey.split('.');
        if (fieldArr.length === 1) {
          if (fieldKey === 'status') {
            self.tableDataAsObject[id][fieldKey] = statusName[value];
          } else if (fieldKey === 'monitoringStatus') {
            self.tableDataAsObject[id][fieldKey] = monitoringStatusString[value ? 1 : 0];
          } else {
            self.tableDataAsObject[id][fieldKey] = value;
          }
        } else if (fieldArr.length === 2) {
          if (fieldKey === 'pricingStrategy.strategyInPercents') {
            self.tableDataAsObject[id][fieldArr[0]].profitType = value === true ? 1 : 2;
          } else {
            self.tableDataAsObject[id][fieldArr[0]][fieldArr[1]] = value;
          }
        }
      });
    },
    checkIfValuesAreNotEqual(item, id) {
      const allAreEqual = item.changedFieldsArray.every(({ fieldKey, value }) => {
        return getOr('', fieldKey, self.tableDataAsObject[id]) === value;
      });
      if (allAreEqual) {
        self.editedItems.clear();
      }
    },
    enterEditMode() {
      self.isEditMode = true;
    },
    cancelEditing() {
      self.isEditMode = false;
      self.editedItems.clear();
    },
  }));

export default Editable;
